import React, {
    useCallback,
    useContext,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

// My Files
import { DatabaseContext, UserContext } from "../../../context";
import ToolbarWithLogo from "../../ToolbarWithLogo";
import UserAvatar from "../UserAvatar";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import firebase from "../../../firebase";

const USER_ROLES = [
    "System Digital Manager",
    "FAW Team Ops",
    "FAW Analyst",
    "FAW Coach",
    "FAW Support Staff",
    "FAW Coach Educator",
    "FAW Coach Ed Student",
    "FAW Player",
    "FAW Parent",
    "Club Digital Manager",
    "Club Administrator",
    "Club Analyst",
    "Club Coach",
    "Club Coach Educator",
    "Club Coach Ed Student",
    "Club Player",
    "Club Parent",
    "Club Support Staff",
    "Referee",
    "Referee Assessor",
];

const pageSx = {
    height: {
        xs: "calc(100vh - 60px)",
        sm: "calc(100vh - 116px)",
    },
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: "background.default",
    pt: { xs: 2, sm: 3, md: 4 },
    pb: { xs: 2, sm: 3 },
    "@supports (height: 100dvh)": {
        height: {
            xs: "calc(100dvh - 60px)",
            sm: "calc(100dvh - 116px)",
        },
    },
};

const cardSx = {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: (theme) =>
        theme.palette.mode === "dark"
            ? "0 8px 28px rgba(0, 0, 0, 0.3)"
            : "0 8px 28px rgba(20, 35, 25, 0.075)",
};

// The list only mounts the rows that fit the scroll viewport, so every row has to
// be exactly this tall for the maths to line up. The name and role are clamped to
// one line each to guarantee it.
const ROW_HEIGHT = 80;
const OVERSCAN_ROWS = 6;

// Every sx object below is hoisted so emotion serialises it once for the whole
// list rather than once per row.
const listSx = { position: "relative" };

const rowSx = {
    position: "absolute",
    left: 0,
    right: 0,
    height: ROW_HEIGHT,
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: { xs: 84, sm: 96 },
        right: { xs: 16, sm: 24 },
        borderBottom: "1px solid",
        borderColor: "divider",
    },
};

const lastRowSx = {
    ...rowSx,
    "&::after": { content: "none" },
};

const rowButtonSx = (theme) => ({
    height: "100%",
    px: { xs: 2, sm: 3 },
    transition: "background-color 150ms ease",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
        outline: `3px solid ${alpha(theme.palette.primary.main, 0.28)}`,
        outlineOffset: -3,
    },
});

const rowAvatarSx = { minWidth: 68 };

const rowChevronSx = { ml: 1, color: "text.disabled" };

const rowTextSlotProps = {
    primary: {
        noWrap: true,
        sx: { fontWeight: 800, color: "text.primary" },
    },
    secondary: {
        noWrap: true,
        sx: { mt: 0.25, color: "text.secondary" },
    },
};

const UserRow = React.memo(function UserRow({
    isLast,
    onSelect,
    rowCount,
    rowIndex,
    user,
}) {
    return (
        <ListItem
            disablePadding
            sx={isLast ? lastRowSx : rowSx}
            style={{ top: rowIndex * ROW_HEIGHT }}
            aria-setsize={rowCount}
            aria-posinset={rowIndex + 1}
        >
            <ListItemButton
                onClick={() => onSelect(user)}
                sx={rowButtonSx}
            >
                <ListItemAvatar sx={rowAvatarSx}>
                    <UserAvatar
                        userOrHub={user}
                        path="users"
                        variant="circular"
                        size="large"
                    />
                </ListItemAvatar>
                <ListItemText
                    primary={`${user.firstName} ${user.lastName}`}
                    secondary={user.customRoleName}
                    slotProps={rowTextSlotProps}
                />
                <ChevronRightIcon aria-hidden="true" sx={rowChevronSx} />
            </ListItemButton>
        </ListItem>
    );
});

function UserRoleFilterDialog({ onApply, onClose, open, selectedRoles }) {
    const [draftRoles, setDraftRoles] = useState(new Set(USER_ROLES));

    useEffect(() => {
        if (open) {
            setDraftRoles(
                new Set(selectedRoles === null ? USER_ROLES : selectedRoles),
            );
        }
    }, [open, selectedRoles]);

    const allRolesSelected = draftRoles.size === USER_ROLES.length;

    const handleToggleRole = (role) => {
        setDraftRoles((currentRoles) => {
            const nextRoles = new Set(currentRoles);

            if (nextRoles.has(role)) {
                nextRoles.delete(role);
            } else {
                nextRoles.add(role);
            }

            return nextRoles;
        });
    };

    const handleToggleAll = () => {
        setDraftRoles(allRolesSelected ? new Set() : new Set(USER_ROLES));
    };

    const handleApply = () => {
        const nextRoles = USER_ROLES.filter((role) => draftRoles.has(role));
        onApply(nextRoles.length === USER_ROLES.length ? null : nextRoles);
    };

    return (
        <Dialog
            onClose={onClose}
            open={open}
            fullWidth
            maxWidth="xs"
            aria-labelledby="user-role-filter-dialog-title"
            slotProps={{
                paper: { sx: { borderRadius: 3, overflow: "hidden" } },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <FilterAltOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="user-role-filter-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Filter users
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose which roles to include in the user list.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close role filter"
                    onClick={onClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1.5,
                        px: 0.5,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {draftRoles.size} of {USER_ROLES.length} roles selected
                    </Typography>
                    <Button
                        size="small"
                        color="inherit"
                        onClick={handleToggleAll}
                    >
                        {allRolesSelected ? "Deselect all" : "Select all"}
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        maxHeight: 410,
                        overflowY: "auto",
                    }}
                >
                    {USER_ROLES.map((role) => (
                        <ListItemButton
                            key={role}
                            onClick={() => handleToggleRole(role)}
                            sx={{
                                py: 0.75,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: (theme) =>
                                    draftRoles.has(role)
                                        ? alpha(theme.palette.primary.main, 0.4)
                                        : theme.palette.divider,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Checkbox
                                checked={draftRoles.has(role)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-label": role }}
                                sx={{ mr: 1 }}
                            />
                            <ListItemText
                                primary={role}
                                primaryTypographyProps={{
                                    sx: { fontWeight: 700 },
                                }}
                            />
                        </ListItemButton>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                <Button color="inherit" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="inherit"
                    onClick={() => {
                        onApply(null);
                    }}
                    disabled={selectedRoles === null}
                >
                    Clear filter
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleApply}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function UserListLoadingState() {
    return (
        <Box component="main" sx={pageSx}>
            <Container
                maxWidth="md"
                sx={{ height: "100%", px: { xs: 1.5, sm: 3 } }}
            >
                <Card
                    variant="outlined"
                    sx={cardSx}
                    role="status"
                    aria-label="Loading users"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            p: { xs: 2.5, sm: 3.5 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.75,
                            }}
                        >
                            <Skeleton variant="rounded" width={48} height={48} />
                            <Box>
                                <Skeleton width={115} height={40} />
                                <Skeleton
                                    height={22}
                                    sx={{ width: { xs: 170, sm: 280 } }}
                                />
                            </Box>
                        </Box>
                        <Skeleton
                            variant="rounded"
                            width={112}
                            height={42}
                            sx={{ display: { xs: "none", sm: "block" } }}
                        />
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: { xs: 2, sm: 2.5 },
                            backgroundColor: "background.subtle",
                        }}
                    >
                        <Skeleton
                            variant="rounded"
                            height={40}
                            sx={{ flex: 1, maxWidth: 520 }}
                        />
                        <Skeleton variant="rounded" width={40} height={40} />
                    </Box>
                    <Divider />
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                        {[0, 1, 2, 3, 4].map((row) => (
                            <Box key={row}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        minHeight: 80,
                                        px: { xs: 2, sm: 3 },
                                    }}
                                >
                                    <Skeleton
                                        variant="circular"
                                        width={56}
                                        height={56}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton width="38%" height={25} />
                                        <Skeleton width="24%" height={20} />
                                    </Box>
                                </Box>
                                {row < 4 && (
                                    <Divider
                                        sx={{ ml: { xs: 10.5, sm: 12 } }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}

// Tracks the scroll offset and height of the list's scroll container so the page
// can work out which rows are actually on screen. Updates are coalesced into an
// animation frame to keep scrolling off the render path.
function useVisibleRange(scrollNode, rowCount) {
    const [metrics, setMetrics] = useState({ scrollTop: 0, height: 0 });

    useEffect(() => {
        if (!scrollNode) {
            return undefined;
        }

        let frame = null;

        const read = () => {
            frame = null;
            setMetrics((current) =>
                current.scrollTop === scrollNode.scrollTop &&
                current.height === scrollNode.clientHeight
                    ? current
                    : {
                          scrollTop: scrollNode.scrollTop,
                          height: scrollNode.clientHeight,
                      },
            );
        };

        const schedule = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(read);
            }
        };

        read();
        scrollNode.addEventListener("scroll", schedule, { passive: true });

        const observer =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(schedule);
        if (observer) {
            observer.observe(scrollNode);
        } else {
            window.addEventListener("resize", schedule);
        }

        return () => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
            scrollNode.removeEventListener("scroll", schedule);
            if (observer) {
                observer.disconnect();
            } else {
                window.removeEventListener("resize", schedule);
            }
        };
    }, [scrollNode]);

    // Moving the container does not synchronously give us a scroll event, so the
    // offset has to be dropped here too or the visible range stays stale for a
    // frame and the list paints blank.
    const scrollToTop = useCallback(() => {
        if (scrollNode) {
            scrollNode.scrollTop = 0;
        }
        setMetrics((current) =>
            current.scrollTop === 0 ? current : { ...current, scrollTop: 0 },
        );
    }, [scrollNode]);

    // Before the first measurement, render enough rows to fill a typical viewport
    // so the list is never blank on the first paint.
    const height = metrics.height || 900;

    const endIndex = Math.min(
        rowCount,
        Math.ceil((metrics.scrollTop + height) / ROW_HEIGHT) + OVERSCAN_ROWS,
    );
    // Clamping against endIndex keeps the window non-empty if the list shrinks
    // while the container is still scrolled down.
    const startIndex = Math.max(
        0,
        Math.min(
            Math.floor(metrics.scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS,
            endIndex - 1,
        ),
    );

    return { startIndex, endIndex, scrollToTop };
}

export default function UserListPage() {
    const history = useHistory();
    const location = useLocation();

    const { users, usersLoaded } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [usernameFilter, setUsernameFilter] = useState("");
    const [roleFilter, setRoleFilter] = useState(null);
    const [roleFilterDialogOpen, setRoleFilterDialogOpen] = useState(false);
    const [userCanEdit, setUserCanEdit] = useState();

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData && currentUserData.permissions) {
            if (currentUserData.permissions.addEditUsers) {
                setUserCanEdit(true);
            } else {
                setUserCanEdit(false);
            }
        }

        return () => unsubscribe();
        // eslint-disable-next-line
    }, [currentUserData, history]);

    // Sorting and case folding are the same for every search term, so do them once
    // per users snapshot instead of on every keystroke. `searchName` is the
    // lowercased name the search box matches against.
    const sortedUsers = useMemo(() => {
        const decorated = users.map((user) => ({
            user,
            searchName: user.fullName.toLowerCase(),
            sortKey: `${user.lastName} ${user.firstName}`.toLowerCase(),
        }));

        decorated.sort((a, b) => {
            if (a.sortKey < b.sortKey) {
                return -1;
            }
            return a.sortKey > b.sortKey ? 1 : 0;
        });

        return decorated;
    }, [users]);

    // Filtering 1900+ users is slow enough to drop keystrokes, so let React keep
    // the input responsive and catch the list up a beat later.
    const deferredUsernameFilter = useDeferredValue(usernameFilter);

    const filteredUsers = useMemo(() => {
        if (!currentUserData) {
            return [];
        }

        const search = deferredUsernameFilter.toLowerCase();
        const roles = roleFilter === null ? null : new Set(roleFilter);
        // A System Digital Manager sees everyone, so check that before walking
        // each user's admins array.
        const seesEveryone = currentUserData.role === "System Digital Manager";

        const matches = [];

        for (const entry of sortedUsers) {
            const user = entry.user;

            if (
                !seesEveryone &&
                !user.admins.includes(currentUserData.key) &&
                user.createdBy !== currentUserData.key
            ) {
                continue;
            }

            if (search && !entry.searchName.includes(search)) {
                continue;
            }

            if (roles !== null && !roles.has(user.role)) {
                continue;
            }

            matches.push(user);
        }

        return matches;
    }, [currentUserData, deferredUsernameFilter, roleFilter, sortedUsers]);

    const [scrollNode, setScrollNode] = useState(null);
    const { startIndex, endIndex, scrollToTop } = useVisibleRange(
        scrollNode,
        filteredUsers.length,
    );

    // A filter change can leave the container scrolled past the end of the new,
    // shorter list, which would show an empty viewport.
    const lastFilters = useRef({
        search: deferredUsernameFilter,
        roles: roleFilter,
    });
    useEffect(() => {
        if (
            lastFilters.current.search !== deferredUsernameFilter ||
            lastFilters.current.roles !== roleFilter
        ) {
            lastFilters.current = {
                search: deferredUsernameFilter,
                roles: roleFilter,
            };
            scrollToTop();
        }
    }, [deferredUsernameFilter, roleFilter, scrollToTop]);

    function handleAddClick() {
        history.push("/newuser");
    }

    const handleManageUserClick = useCallback(
        (user) => {
            history.push({
                pathname: `/manageuser/${user.key}`,
                user: user,
            });
        },
        [history],
    );

    const handleChangeFilterText = (event) => {
        setUsernameFilter(event.target.value);
    };

    const handleApplyRoleFilter = (nextRoles) => {
        setRoleFilter(nextRoles);
        setRoleFilterDialogOpen(false);
    };

    const clearFilters = () => {
        setUsernameFilter("");
        setRoleFilter(null);
    };

    const hasActiveRoleFilter = roleFilter !== null;
    const hasActiveFilters = Boolean(usernameFilter) || hasActiveRoleFilter;
    const isLoading = !usersLoaded || userCanEdit === undefined;

    return (
        <>
            {!isLoading && userCanEdit && (
                <Box component="main" sx={pageSx}>
                    <Container
                        maxWidth="md"
                        sx={{ height: "100%", px: { xs: 1.5, sm: 3 } }}
                    >
                        <Card variant="outlined" sx={cardSx}>
                            <Box
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    flexShrink: 0,
                                    backgroundColor: "background.paper",
                                }}
                            >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "flex-start", sm: "center" },
                                    justifyContent: "space-between",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2,
                                    p: { xs: 2.5, sm: 3.5 },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.75,
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            display: "grid",
                                            placeItems: "center",
                                            flexShrink: 0,
                                            color: "primary.main",
                                            backgroundColor: (theme) =>
                                                alpha(
                                                    theme.palette.primary.main,
                                                    0.11,
                                                ),
                                            border: "1px solid",
                                            borderColor: (theme) =>
                                                alpha(
                                                    theme.palette.primary.main,
                                                    0.18,
                                                ),
                                        }}
                                    >
                                        <GroupOutlinedIcon />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            sx={{
                                                fontSize: { xs: 27, sm: 32 },
                                                lineHeight: 1.15,
                                            }}
                                        >
                                            Users
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.5,
                                                color: "text.secondary",
                                            }}
                                        >
                                            Find and manage the people you have
                                            access to.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<AddIcon />}
                                    onClick={handleAddClick}
                                    sx={{
                                        flexShrink: 0,
                                        alignSelf: {
                                            xs: "stretch",
                                            sm: "center",
                                        },
                                        px: 2.25,
                                        py: 1.1,
                                        fontWeight: 700,
                                    }}
                                >
                                    Add user
                                </Button>
                            </Box>

                            <Divider />

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "stretch", sm: "center" },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 1.5,
                                    p: { xs: 2, sm: 2.5 },
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        width: "100%",
                                        maxWidth: { sm: 572 },
                                    }}
                                >
                                    <TextField
                                        id="user-name-filter"
                                        label="Search users"
                                        placeholder="Type a user name"
                                        fullWidth
                                        size="small"
                                        value={usernameFilter}
                                        onChange={handleChangeFilterText}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            sx={{
                                                                color: "text.secondary",
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: usernameFilter ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label="Clear user search"
                                                            onClick={() =>
                                                                setUsernameFilter("")
                                                            }
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                        sx={{
                                            minWidth: 0,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor:
                                                    "background.paper",
                                            },
                                        }}
                                    />
                                    <Tooltip title="Filter users by role">
                                        <IconButton
                                            aria-label="Filter users by role"
                                            aria-pressed={hasActiveRoleFilter}
                                            onClick={() =>
                                                setRoleFilterDialogOpen(true)
                                            }
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                flexShrink: 0,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: hasActiveRoleFilter
                                                    ? "primary.main"
                                                    : "text.disabled",
                                                color: hasActiveRoleFilter
                                                    ? "primary.main"
                                                    : "text.secondary",
                                                backgroundColor:
                                                    "background.paper",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "action.hover",
                                                },
                                            }}
                                        >
                                            <Badge
                                                color="primary"
                                                variant="dot"
                                                invisible={!hasActiveRoleFilter}
                                            >
                                                <FilterAltOutlinedIcon />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Chip
                                    label={`${filteredUsers.length} ${
                                        filteredUsers.length === 1
                                            ? "user"
                                            : "users"
                                    }`}
                                    variant="outlined"
                                    sx={{
                                        alignSelf: { xs: "flex-start", sm: "center" },
                                        flexShrink: 0,
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                        color: "text.secondary",
                                        fontWeight: 700,
                                    }}
                                />
                            </Box>

                            <Divider />
                            </Box>

                            <Box
                                ref={setScrollNode}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    overscrollBehavior: "contain",
                                }}
                            >
                            {filteredUsers.length > 0 ? (
                                <List
                                    disablePadding
                                    aria-label="Users"
                                    sx={listSx}
                                    style={{
                                        height: filteredUsers.length * ROW_HEIGHT,
                                    }}
                                >
                                    {filteredUsers
                                        .slice(startIndex, endIndex)
                                        .map((user, offset) => {
                                            const rowIndex =
                                                startIndex + offset;

                                            return (
                                                <UserRow
                                                    key={user.key}
                                                    user={user}
                                                    rowIndex={rowIndex}
                                                    rowCount={
                                                        filteredUsers.length
                                                    }
                                                    isLast={
                                                        rowIndex ===
                                                        filteredUsers.length - 1
                                                    }
                                                    onSelect={
                                                        handleManageUserClick
                                                    }
                                                />
                                            );
                                        })}
                                </List>
                            ) : (
                                <Box
                                    role="status"
                                    sx={{
                                        px: 3,
                                        py: { xs: 6, sm: 8 },
                                        textAlign: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 58,
                                            height: 58,
                                            mx: "auto",
                                            mb: 2,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            color: "primary.main",
                                            backgroundColor: "action.hover",
                                        }}
                                    >
                                        <PersonSearchOutlinedIcon />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        component="h2"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        No users found
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 0.75,
                                            color: "text.secondary",
                                        }}
                                    >
                                        {hasActiveFilters
                                            ? "Try changing or clearing your search and role filters."
                                            : "There are no users available to manage yet."}
                                    </Typography>
                                    {hasActiveFilters && (
                                        <Button
                                            variant="outlined"
                                            onClick={clearFilters}
                                            sx={{ mt: 2.5 }}
                                        >
                                            Clear filters
                                        </Button>
                                    )}
                                </Box>
                            )}
                            </Box>
                        </Card>
                    </Container>
                </Box>
            )}
            {!isLoading && userCanEdit === false && <NoPermissionsMessage />}
            {isLoading && <UserListLoadingState />}
            <UserRoleFilterDialog
                open={roleFilterDialogOpen}
                onClose={() => setRoleFilterDialogOpen(false)}
                onApply={handleApplyRoleFilter}
                selectedRoles={roleFilter}
            />
            <ToolbarWithLogo />
        </>
    );
}
